import { useMemo } from "react";
import { MetricCard } from "@/components/cards/MetricCard";
import { EngagementChart } from "@/components/charts/EngagementChart";
import { PieChartCard } from "@/components/charts/PieChartCard";
import { BarChartCard } from "@/components/charts/BarChartCard";
import { FilterBar } from "@/components/layout/FilterBar";
import { PageHeader } from "@/components/layout/PageHeader";
import { useFilteredGA4 } from "@/hooks/useFilteredData";
import { fillDateRange } from "@/lib/chart-utils";
import { Users, Eye, Target, Timer, TrendingUp } from "lucide-react";
import { LoadingState } from '@/components/ui/spinner';

export default function Ga4Page() {
  const { data, accounts, filters, setFilters, loading } = useFilteredGA4();

  const totalSessions = data.reduce((s, d) => s + d.sessions, 0);
  const totalUsers = data.reduce((s, d) => s + d.totalusers, 0);
  const totalPageViews = data.reduce((s, d) => s + d.screen_page_views, 0);
  const avgBounceRate = data.length > 0 ? data.reduce((s, d) => s + d.bounce_rate, 0) / data.length : 0;
  const totalConversions = data.reduce((s, d) => s + d.conversions, 0);
  const avgEngagementRate = data.length > 0 ? data.reduce((s, d) => s + d.engagement_rate, 0) / data.length : 0;

  const sessionsTrend = useMemo(() => {
    const map = new Map<string, number>();
    data.forEach((d) => map.set(d.date, (map.get(d.date) || 0) + d.sessions));
    const raw = Array.from(map.entries()).map(([date, value]) => ({ date, value }));
    return fillDateRange(raw, filters.dateFrom, filters.dateTo);
  }, [data, filters.dateFrom, filters.dateTo]);

  const usersTrend = useMemo(() => {
    const map = new Map<string, { users: number; newUsers: number }>();
    data.forEach((d) => {
      const curr = map.get(d.date) || { users: 0, newUsers: 0 };
      curr.users += d.totalusers; curr.newUsers += d.newusers;
      map.set(d.date, curr);
    });
    const raw = Array.from(map.entries()).map(([date, { users, newUsers }]) => ({ date, value: users, value2: newUsers }));
    return fillDateRange(raw, filters.dateFrom, filters.dateTo);
  }, [data, filters.dateFrom, filters.dateTo]);

  const channelData = useMemo(() => {
    const map = new Map<string, number>();
    data.forEach((d) => map.set(d.default_channel_group, (map.get(d.default_channel_group) || 0) + d.sessions));
    return Array.from(map.entries()).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value);
  }, [data]);

  const deviceData = useMemo(() => {
    const map = new Map<string, number>();
    data.forEach((d) => map.set(d.devicecategory, (map.get(d.devicecategory) || 0) + d.sessions));
    return Array.from(map.entries()).map(([name, value]) => ({ name, value }));
  }, [data]);

  const accountData = useMemo(() => {
    const map = new Map<string, { sessions: number; conversions: number }>();
    data.forEach((d) => {
      const curr = map.get(d.account_name) || { sessions: 0, conversions: 0 };
      curr.sessions += d.sessions; curr.conversions += d.conversions;
      map.set(d.account_name, curr);
    });
    return Array.from(map.entries()).map(([name, { sessions, conversions }]) => ({ name: name.replace(" - GA4", ""), value: sessions, value2: conversions }));
  }, [data]);

  if (loading) return <LoadingState label="Načítám data…" />;

  return (
    <div className="space-y-6">
      <PageHeader title="GA4 Analytika" description="Přehled návštěvnosti a výkonu webů" />

      <FilterBar filters={filters} onChange={setFilters} accounts={accounts} />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        <MetricCard label="Sessions" value={totalSessions} icon={<Users className="h-4 w-4 text-primary" />} />
        <MetricCard label="Uživatelé" value={totalUsers} icon={<Users className="h-4 w-4 text-primary" />} />
        <MetricCard label="Zobrazení stránek" value={totalPageViews} icon={<Eye className="h-4 w-4 text-primary" />} />
        <MetricCard label="Bounce Rate" value={avgBounceRate} format="percent" icon={<TrendingUp className="h-4 w-4 text-primary" />} />
        <MetricCard label="Engagement Rate" value={avgEngagementRate} format="percent" icon={<Timer className="h-4 w-4 text-primary" />} />
        <MetricCard label="Konverze" value={totalConversions} icon={<Target className="h-4 w-4 text-primary" />} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <EngagementChart title="Sessions trend" data={sessionsTrend} color="#7c9a5e" label1="Sessions" />
        <EngagementChart title="Uživatelé vs Noví" data={usersTrend} dataKey="value" dataKey2="value2" color="#6b8fd4" color2="#e8c36a" label1="Uživatelé" label2="Noví" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <PieChartCard title="Kanály" data={channelData} />
        <PieChartCard title="Zařízení" data={deviceData} />
        <BarChartCard title="Porovnání webů" data={accountData} color="#7c9a5e" color2="#e8c36a" label1="Sessions" label2="Konverze" />
      </div>
    </div>
  );
}
